import React, { ReactNode } from 'react';
import AppConfig from '~/utils/appconfig';

export enum Features {
    ADAPT_WORKFLOW = 'adapt-workflow',
    DOSE_WORKFLOW = 'dose-workflow',
    CONTOUR_WORKFLOW = 'contour-workflow',
    DATA_UPLOAD = 'data-upload',
    SEG_VERIFY = 'verify',
    IMAGE_TRANSLATION_WORKFLOW = 'image-translation-workflow',
    OPTIMIZATION_OBJECTIVES = 'optimization-objectives',
}

/**
 * Every feature that contributes a tab to the workflow modal, in either the
 * backend or the connector variant.
 */
const WORKFLOW_FEATURES = [
    Features.CONTOUR_WORKFLOW,
    Features.DOSE_WORKFLOW,
    Features.IMAGE_TRANSLATION_WORKFLOW,
    Features.ADAPT_WORKFLOW,
    Features.SEG_VERIFY,
    Features.DATA_UPLOAD,
] as const;

/**
 * True when the workflow modal would have at least one tab to show.
 *
 * The modal is built entirely from these flags, so with all of them off it opens
 * with no tabs, no titles and nothing to fetch -- a blank dialog. Anything that
 * opens it should check this first. A deployment that grants no models turns them
 * all off, because every workflow submits a job that would fail.
 */
export const hasAnyWorkflowFeature = (): boolean =>
    WORKFLOW_FEATURES.some((feature) => AppConfig.isFeatureEnabled(feature));

interface FeatureToggleProps {
    feature: Features;
    fallback?: ReactNode;
    children: ReactNode;
}

const FeatureToggle: React.FC<FeatureToggleProps> = ({
    feature,
    fallback = null,
    children,
}) => {
    const isEnabled = AppConfig.isFeatureEnabled(feature);
    return isEnabled ? <>{children}</> : <>{fallback}</>;
};

export default FeatureToggle;
